import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import GenericProductsApi from '~/src/services/api/GenericProductsApi';
import { ProductInStockPeriodDto } from '~/src/types/ProductInStockPeriodDto';
import DisplayDate from '../../../../components/common/DisplayDate';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import PuffLoader from 'react-spinners/PuffLoader';

interface ProductInStockModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  productId: string;
  warehouseId?: string;
}

const ProductInStockModal: React.FC<ProductInStockModalProps> = ({ isOpen, onRequestClose, productId, warehouseId }) => {
  const [productInStockData, setProductInStockData] = useState<ProductInStockPeriodDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchProductInStockData = async () => {
      setLoading(true);
      try {
        const response = warehouseId
          ? await GenericProductsApi.getProductInStockPeriodInWarehouse(warehouseId, productId)
          : await GenericProductsApi.getProductInStockPeriod(productId);
        if (response.data !== null) {
          setProductInStockData(response.data);
        } else {
          setProductInStockData([]);
        }
      } catch (error) {
        console.error('Failed to fetch product in stock data', error);
        setProductInStockData([]);
      }
      setLoading(false);
    };

    if (isOpen) {
      fetchProductInStockData();
    }
  }, [isOpen, productId]);

  const handleClose = () => {
    setProductInStockData([]);
    onRequestClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Product In Stock Information"
      style={modalStyles}
    >
      <ModalContent>
        <Header>
          <h2>Product In Stock Information</h2>
          <SecondaryButton title="Close" onClick={handleClose} />
        </Header>
        {loading ? (
          <LoaderContainer>
            <PuffLoader color="#000000" loading={true} size={50} />
          </LoaderContainer>
        ) : productInStockData.length > 0 ? (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Purchase Order Number</th>
                  <th>Generic Product</th>
                  <th>Stock Date</th>
                  <th>Warehouse Owner</th>
                  <th>Supplier</th>
                  <th>Days In Stock</th>
                  <th>Expiration Date</th>
                  <th>Available Units</th>
                  <th>Reserved Units</th>
                  <th>In Stock Units</th>
                </tr>
              </thead>
              <tbody>
                {productInStockData.map((item) => (
                  <tr key={item.purchaseOrderNumber}>
                    <td>{item.purchaseOrderNumber}</td>
                    <td>{item.genericProductDto.name}</td>
                    <td><DisplayDate date={item?.stockDateTime} /></td>
                    <td>{item?.warehouseOwnerDto?.name}</td>
                    <td>{item?.supplierDto?.name}</td>
                    <td>{item.daysInStock}</td>
                    <td><DisplayDate date={item.expirationDate} /></td>
                    <td>{`${item.availableUnitsQuantity.duUnits}/${item.availableUnitsQuantity.cuUnits}/${item.availableUnitsQuantity.dpUnits}`}</td>
                    <td>{`${item.reservedUnitsQuantity.duUnits}/${item.reservedUnitsQuantity.cuUnits}/${item.reservedUnitsQuantity.dpUnits}`}</td>
                    <td>{`${item.inStockUnitsQuantity.duUnits}/${item.inStockUnitsQuantity.cuUnits}/${item.inStockUnitsQuantity.dpUnits}`}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        ) : (
          <p>No available information.</p>
        )}
      </ModalContent>
    </Modal>
  );
};

const modalStyles = {
  content: {
    width: '70%',
    height: '600px',
    margin: 'auto',
    borderRadius: '20px',
  },
};

const ModalContent = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 20px;
  padding-top: 0;
  padding-bottom: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TableContainer = styled.div`
  max-height: 350px;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th, td {
    border: 1px solid #ddd;
    padding: 8px;
  }

  th {
    background-color:rgb(242, 242, 242);
    text-align: left;
    position: sticky;
    top: 0;
    z-index: 1;
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4);
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export default ProductInStockModal;